import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import {
	CCButton,
	CCCheckbox,
	CCField,
	CCSelect,
	CCSwitch,
} from "@pymblecitizencoders/cc-ui";
import { ChevronDown, ChevronsLeft, ChevronsRight, X } from "lucide-react";
import { CalendarEvent, EventFilterState } from "../../types/event";
import { DEFAULT_CATEGORY_IDS } from "../../utils/eventFilterStorage";
import {
	buildFacets,
	FacetOption,
	sameSelection,
} from "../../utils/eventFacets";
import { SCHOOL_DAY_LABEL } from "../../utils/schoolDay";
import { Tooltip } from "../common/Tooltip";
import { useEventTags } from "../../hooks/useEventTags";
import { EventTagFilter } from "./EventTagFilter";
import "./eventManager.css";

interface EventFiltersProps {
	filters: EventFilterState;
	/** The events fetched for the visible range — what every count is over. */
	events: CalendarEvent[];
	/**
	 * True when `events` came back from a server-side search, so the term must
	 * not be applied to them a second time. See FacetOptions.
	 */
	searchApplied?: boolean;
	onFilterChange: <K extends keyof EventFilterState>(
		key: K,
		value: EventFilterState[K],
	) => void;
	onClearAll: () => void;
	collapsed: boolean;
	onToggleCollapse: () => void;
	/**
	 * The bump in / bump out display switch, present only where there is
	 * something for it to act on — the three calendar views, not List or Cards.
	 *
	 * Optional, and absent where it would do nothing: the rail draws what the
	 * page can currently do, rather than a disabled control explaining why it
	 * cannot.
	 *
	 * NOT PART OF `filters`, and that is the whole reason it is passed
	 * separately rather than added to EventFilterState. It hides a band, never
	 * an event: it must not count towards "N filters applied", must not be
	 * cleared by "Clear all", and must not join the query key the API is asked
	 * with — which is what folding it in would do, making a band toggle refetch
	 * the month.
	 */
	showBumpTimes?: boolean;
	onShowBumpTimes?: (next: boolean) => void;
}

/**
 * What each toggle means, in the tooltip on its `?`.
 *
 * Constants because each is its switch's accessible name, its `title` and the
 * tooltip's text — three places that must not drift into three wordings of the
 * same sentence.
 *
 * THE FIRST NAMES THE SOURCE FIRST. "Unapproved" is true of every Edumate
 * lesson and Clipboard booking on the calendar, since neither is ever submitted
 * for release, so a label that stopped at the word would promise to hide the
 * timetable. What the filter actually does is narrow OUR events to the ones
 * that are out.
 *
 * THE SECOND NAMES THE EDGE CASE, because "during the school day" is the half
 * of it anyone can guess. What they cannot guess is whether an excursion
 * running past the last bell counts, and it does.
 */
const UNAPPROVED_HELP =
	"Hides Event Manager events that sign-off has not released: not submitted, in review, sent back or declined. Edumate and Clipboard events are always shown.";

/**
 * THE LAST SENTENCE IS THE POINT. This switch sits inches from two that take
 * events off the calendar, and the one thing somebody has to know about it is
 * that it does not.
 */
const BUMP_HELP =
	"Draws the crew's setup and pack-down time as a hatched band either side of the event, in the Week and Day views, and names both times in the hover card. Changes what is drawn, not which events are shown.";

const SCHOOL_DAY_HELP = `Keeps only events that touch the school day, between ${SCHOOL_DAY_LABEL}. An excursion that leaves earlier or returns later still counts, and so does an all-day event; something wholly outside it — an early squad session, an evening concert — does not.`;

const PERMISSION_HELP =
	"Keeps only events that ask parents for permission. Events with no permission request, including Edumate and Clipboard rows, are hidden.";

/** How many audience rows show before "Show all" — the rest are a search away. */
const GROUP_PREVIEW = 8;

/**
 * One switch in the rail, with a `?` that explains it.
 *
 * The `?` is OUTSIDE the switch, not in its label: CCSwitch renders its label
 * inside the button so the whole row is one target, and a `?` in there would
 * toggle the filter when someone went to read what it meant — as well as
 * becoming part of the switch's accessible name.
 *
 * `interactive`, so the Tooltip leaves the button in the accessibility tree and
 * lets it name itself. The name is the whole explanation rather than "help",
 * because a `?` names nothing and cc-ui's guidance is that `title` cannot carry
 * meaning: unavailable on touch, unreliable with a screen reader. It is set as
 * well, as the touch fallback for a hover-only tooltip, exactly as
 * .em-signoff__why does.
 */
const ToggleRow: React.FC<{
	id: string;
	label: string;
	help: string;
	checked: boolean;
	onChange: (next: boolean) => void;
}> = ({ id, label, help, checked, onChange }) => (
	<div className="em-filter-toggle">
		<CCSwitch id={id} label={label} checked={checked} onChange={onChange} />
		<Tooltip label={help} interactive>
			<button
				type="button"
				className="em-filter-help"
				aria-label={help}
				title={help}
			>
				<span aria-hidden="true">?</span>
			</button>
		</Tooltip>
	</div>
);

/** Add or remove one id from a facet's selection. */
const toggle = (list: string[], id: string): string[] =>
	list.includes(id) ? list.filter((v) => v !== id) : [...list, id];

/**
 * One tick box in a facet: name on the left, how many events on the right.
 *
 * The count is a plain span rather than a badge — the design puts it in the
 * link colour at the row's end, where it reads as an amount rather than a
 * status. `counted: false` prints nothing at all: see FacetOption on why a
 * zero would be a lie for an unticked source.
 */
const FacetRow: React.FC<{
	idPrefix: string;
	option: FacetOption;
	checked: boolean;
	onChange: () => void;
}> = ({ idPrefix, option, checked, onChange }) => (
	<div className="em-facet-row">
		<CCCheckbox
			id={`${idPrefix}-${option.id}`}
			checked={checked}
			onChange={onChange}
			label={
				<>
					{option.colour && (
						<span
							className="em-category-swatch"
							style={{ backgroundColor: option.colour }}
						/>
					)}
					{option.label}
				</>
			}
		/>
		{option.counted && (
			<span className="em-facet-row__count" aria-hidden="true">
				{option.count}
			</span>
		)}
		{option.counted && (
			<span className="em-sr-only">{option.count} events</span>
		)}
	</div>
);

/** A named block of tick boxes, with an optional control beside its name. */
const Facet: React.FC<{
	title: string;
	action?: React.ReactNode;
	children: React.ReactNode;
}> = ({ title, action, children }) => (
	<div className="em-facet">
		<h6 className="em-facet__title">
			{title}
			{action}
		</h6>
		{children}
	</div>
);

/**
 * One of the two "Advanced" sections, which open and close.
 *
 * A real <button> holding the heading rather than a <details>: the chevron has
 * to sit at the far end of the row, and `::marker` cannot be put there.
 */
const RailSection: React.FC<{
	title: string;
	open: boolean;
	onToggle: () => void;
	children: React.ReactNode;
}> = ({ title, open, onToggle, children }) => {
	const bodyId = `em-rail-${title.replace(/\W+/g, "-").toLowerCase()}`;
	return (
		<section className="em-rail-section">
			<h5 className="em-rail-section__head">
				<button
					type="button"
					className="em-rail-section__toggle"
					aria-expanded={open}
					aria-controls={bodyId}
					onClick={onToggle}
				>
					<span>{title}</span>
					<ChevronDown
						size={16}
						aria-hidden="true"
						className={
							open
								? "em-rail-section__chev em-rail-section__chev--open"
								: "em-rail-section__chev"
						}
					/>
				</button>
			</h5>
			<div id={bodyId} hidden={!open} className="em-rail-section__body">
				{children}
			</div>
		</section>
	);
};

/**
 * Left filter rail on the calendar page.
 *
 * Structure follows the rail design: a header that can collapse the whole
 * thing, a free-text search, a location select, a line saying how much is
 * narrowing the calendar with a way to undo it, then two collapsible
 * "Advanced" sections of faceted tick boxes with counts.
 *
 * The FACETS are not the design's — Area, School section, Cost and Access have
 * no field on the S2 event document to filter on, and a tick box that cannot
 * filter is worse than none. See eventFacets for what replaced them and why.
 *
 * Counts are over the events the calendar has fetched for the range on screen,
 * which is the only set this app has; each one says what ticking that row would
 * leave you with.
 */
export const EventFilters: React.FC<EventFiltersProps> = ({
	filters,
	events,
	searchApplied,
	onFilterChange,
	onClearAll,
	collapsed,
	onToggleCollapse,
	showBumpTimes,
	onShowBumpTimes,
}) => {
	const [detailsOpen, setDetailsOpen] = useState(true);
	const [visibilityOpen, setVisibilityOpen] = useState(true);
	const [groupSearch, setGroupSearch] = useState("");
	const [showAllGroups, setShowAllGroups] = useState(false);

	/* The tag rows come from the catalogue rather than from `events`, because
	   ticking one filters at the API and the response then holds only events
	   carrying it — a list built from what is on screen would shed every other
	   tag the moment one was used, leaving no way to widen the selection. */
	const { tags: tagCatalogue } = useEventTags();

	const facets = useMemo(
		() => buildFacets(events, filters, { searchApplied, tagCatalogue }),
		[events, filters, searchApplied, tagCatalogue],
	);

	const matchingGroups = useMemo(() => {
		const needle = groupSearch.trim().toLowerCase();
		if (!needle) return facets.groups;
		return facets.groups.filter((g) => g.label.toLowerCase().includes(needle));
	}, [facets.groups, groupSearch]);

	/* `count` in the label, not beside it: a dropdown row is one string. The
	   unknown-count case the tick boxes render as a blank — see FacetRow — has
	   to say something here, so it says nothing at all rather than "(0)", which
	   would read as "no events of this type" when it means "not knowable while
	   another facet is ticked". */
	const eventTypeOptions = useMemo(
		() =>
			facets.eventTypes.map((t) => ({
				value: t.id,
				label: t.counted ? `${t.label} (${t.count})` : t.label,
			})),
		[facets.eventTypes],
	);

	const shownGroups =
		showAllGroups || groupSearch.trim()
			? matchingGroups
			: matchingGroups.slice(0, GROUP_PREVIEW);

	if (collapsed) {
		return (
			<div
				className="em-panel em-filter-rail em-filter-rail--collapsed"
				data-surface="card"
			>
				<button
					type="button"
					className="em-filter-rail__expand"
					onClick={onToggleCollapse}
					title="Show filters"
				>
					<ChevronsRight size={16} aria-hidden="true" />
					<span className="em-filter-rail__expand-text">Filters</span>
					{facets.appliedCount > 0 && (
						<span className="em-filter-rail__count">{facets.appliedCount}</span>
					)}
					<span className="em-sr-only">
						Show filters
						{facets.appliedCount > 0 ? `, ${facets.appliedCount} applied` : ""}
					</span>
				</button>
			</div>
		);
	}

	return (
		<div className="em-panel em-filter-rail" data-surface="card">
			<div className="em-filter-rail__head">
				<h5 className="em-filter-rail__title">Filters</h5>
				<button
					type="button"
					className="em-filter-rail__collapse"
					onClick={onToggleCollapse}
					title="Hide filters"
				>
					Collapse
					<ChevronsLeft size={14} aria-hidden="true" />
				</button>
			</div>

			{/* `type="search"` gives WebKit and Blink a clear affordance of their
			    own, but it is unstyled, absent in Firefox, and sized for a
			    full-height field rather than these small ones — so the browser's
			    is hidden in CSS and this is drawn instead, present in every
			    browser and only while there is something to clear. */}
			<div className="em-filter-field">
				<CCField
					label="Search events"
					type="search"
					size="sm"
					fullWidth
					value={filters.searchText}
					onChange={(e) => onFilterChange("searchText", e.target.value)}
				/>
				{filters.searchText && (
					<button
						type="button"
						className="em-filter-clear"
						title="Clear search"
						onClick={() => onFilterChange("searchText", "")}
					>
						<X size={14} aria-hidden="true" />
						<span className="em-sr-only">Clear search</span>
					</button>
				)}
			</div>

			{/* EVENT TYPE HAS THIS SLOT, which a Location select used to hold.
			    Location went rather than moving down: the rail has room for one
			    control above the fold, this is the one people reach for, and
			    filtering on a venue's Edumate spelling was never how anyone found
			    an event. Nothing about the data changed — `Location` is still on
			    the document and still shown wherever an event is.

			    ONE VALUE, from a select. It was a multi-select, and the list was
			    not earning itself: the types are alternatives rather than
			    additive, so nobody chose two, and choosing them all is the same as
			    choosing none. The list did cost something visible — CCMultiSelect
			    hangs a tag strip under the control that reads "No selections"
			    whenever nothing is chosen, which is a line of the rail spent
			    announcing that a filter is off.

			    The empty value IS the no-filter state and shows the placeholder,
			    the same way the Location select this replaced worked.

			    The count rides in the option label, since a dropdown row has no
			    column to put it in.

			    Rendered whatever the options hold, unlike in Advanced where it was
			    gated on `facets.eventTypes.length`: a control that comes and goes
			    from the top of the rail as the range changes is worse than an empty
			    one, and "All types" over no options still reads correctly. */}
			<CCSelect
				label="Event type"
				size="sm"
				fullWidth
				placeholder="All types"
				options={eventTypeOptions}
				value={filters.eventType}
				onChange={(e) => onFilterChange("eventType", e.target.value)}
			/>

			{/* Above the fold rather than inside Advanced, and a switch rather
			    than another tick box: this is a MODE over the whole calendar, not
			    one option among a counted list, and it is the only filter here
			    that can take most of the grid away at once. Somewhere it can be
			    turned off without remembering which section it was in.

			    A CCSwitch for the same reason the sign-off screen uses one for
			    its "Not approved only" — the two controls are the same idea from
			    opposite ends, and they should not look like different kinds of
			    thing. */}
			<div className="em-filter-toggles">
				{/* LABELS ARE SHORT ON PURPOSE. The rail is a fixed ~250px, and a
				    label wider than what is left after the track and the `?` does
				    not overflow — the switch takes the whole row, the words wrap to
				    two lines and the `?` ends up stranded against the far edge.
				    "Hide unapproved events" did exactly that. Each `?` carries the
				    full sentence, so the label only has to be recognisable. */}
				<ToggleRow
					id="em-filter-hide-unapproved"
					label="Hide unapproved"
					help={UNAPPROVED_HELP}
					checked={filters.hideUnapproved}
					onChange={(next) => onFilterChange("hideUnapproved", next)}
				/>
				<ToggleRow
					id="em-filter-school-day"
					label="School day only"
					help={SCHOOL_DAY_HELP}
					checked={filters.schoolDayOnly}
					onChange={(next) => onFilterChange("schoolDayOnly", next)}
				/>
				<ToggleRow
					id="em-filter-permission"
					label="Needs permission"
					help={PERMISSION_HELP}
					checked={filters.permissionOnly}
					onChange={(next) => onFilterChange("permissionOnly", next)}
				/>
			</div>

			<div className="em-filter-applied">
				<span className="em-filter-applied__count">
					{facets.appliedCount === 0
						? "No filters applied"
						: `${facets.appliedCount} filter${facets.appliedCount === 1 ? "" : "s"} applied`}
				</span>
				<button
					type="button"
					className="em-filter-applied__clear"
					onClick={onClearAll}
					disabled={facets.appliedCount === 0}
				>
					Clear all
				</button>
			</div>

			{/* BELOW THE CLEAR-ALL ROW, and that placement is the argument. Every
			    control above it is counted by "N filters applied" and undone by
			    "Clear all"; this one is neither. Putting it in
			    `em-filter-toggles` with the other two switches would make it look
			    like a third filter, and the first time "Clear all" left it
			    untouched that would read as a bug.

			    Its own heading for the same reason. One word is enough to say
			    these are a different kind of question — what the calendar draws,
			    not which events it draws. */}
			{onShowBumpTimes && (
				<div className="em-filter-display">
					<h6 className="em-filter-display__head">Display</h6>
					<ToggleRow
						id="em-filter-bump-times"
						label="Bump times"
						help={BUMP_HELP}
						checked={showBumpTimes === true}
						onChange={onShowBumpTimes}
					/>
				</div>
			)}

			<RailSection
				title="Advanced: Details"
				open={detailsOpen}
				onToggle={() => setDetailsOpen((v) => !v)}
			>
				<Facet
					title="Event source"
					action={
						/* Restores the DEFAULT selection, not every source: ticking
						   everything means ~1,700 events a week of timetable and
						   co-curricular bookings, which is the state this rail exists
						   to get out of. */
						<button
							type="button"
							className="em-filter-icon-btn"
							title="Reset event sources"
							aria-label="Reset event sources"
							disabled={sameSelection(filters.categories, DEFAULT_CATEGORY_IDS)}
							onClick={() =>
								onFilterChange("categories", [...DEFAULT_CATEGORY_IDS])
							}
						>
							<FontAwesomeIcon icon={faRotateLeft} />
						</button>
					}
				>
					{facets.sources.map((opt) => (
						<FacetRow
							key={opt.id}
							idPrefix="em-src"
							option={opt}
							checked={filters.categories.includes(opt.id)}
							onChange={() =>
								onFilterChange("categories", toggle(filters.categories, opt.id))
							}
						/>
					))}
					{filters.categories.length === 0 && (
						/* Says why the calendar is empty. Without it an all-unticked
						   source list looks like a loading failure. */
						<span className="em-muted">
							No event sources selected — nothing will show.
						</span>
					)}
				</Facet>

				{/* SERVER-SIDE, like the sources above and unlike everything else
				    in this rail: choosing a tag refetches the range with a `tags=`
				    parameter rather than filtering the events already in hand.

				    Which is why a count can go missing from the list. With nothing
				    chosen every tally is exact; choose one and the response holds
				    only events carrying it, so the rest are unknowable and print
				    no number rather than a zero that would read as "none this
				    week".

				    Filtering only. Adding tags to events is the "Tag events" bar
				    over the calendar — see EventTagFilter on why the two were
				    pulled apart. */}
				{facets.tags.length > 0 && (
					<div className="em-facet em-facet--control">
						<EventTagFilter
							options={facets.tags}
							selected={filters.tags}
							onChange={(tags) => onFilterChange("tags", tags)}
						/>
					</div>
				)}
			</RailSection>

			<RailSection
				title="Advanced: Visibility"
				open={visibilityOpen}
				onToggle={() => setVisibilityOpen((v) => !v)}
			>
				{facets.groups.length === 0 ? (
					<span className="em-muted">No audiences on the events shown.</span>
				) : (
					<>
						<div className="em-filter-field">
							<CCField
								label="Search audiences"
								type="search"
								size="sm"
								fullWidth
								value={groupSearch}
								onChange={(e) => setGroupSearch(e.target.value)}
							/>
							{groupSearch && (
								<button
									type="button"
									className="em-filter-clear"
									title="Clear audience search"
									onClick={() => setGroupSearch("")}
								>
									<X size={14} aria-hidden="true" />
									<span className="em-sr-only">Clear audience search</span>
								</button>
							)}
						</div>
						<span className="em-facet__hint">
							{/* The number is the audiences ON SCREEN, not the school's
							    thousand-odd groups: this searches what the events in
							    view are addressed to, and saying otherwise would
							    promise a group search this rail does not do. */}
							{facets.groups.length} audience
							{facets.groups.length === 1 ? "" : "s"} on the events shown
						</span>

						{shownGroups.length === 0 ? (
							<span className="em-muted">No audience matches that.</span>
						) : (
							shownGroups.map((opt) => (
								<FacetRow
									key={opt.id}
									idPrefix="em-grp"
									option={opt}
									checked={filters.groups.includes(opt.id)}
									onChange={() =>
										onFilterChange("groups", toggle(filters.groups, opt.id))
									}
								/>
							))
						)}

						{!groupSearch.trim() && matchingGroups.length > GROUP_PREVIEW && (
							<CCButton
								variant="ghost"
								size="sm"
								onClick={() => setShowAllGroups((v) => !v)}
							>
								{showAllGroups
									? "Show fewer"
									: `Show all ${matchingGroups.length}`}
							</CCButton>
						)}
					</>
				)}
			</RailSection>
		</div>
	);
};
